import React, {useEffect, useState} from "react";
import { AlertTriangle, Plus } from "lucide-react";
import { Modal, VbButton, Callout, Field, Input, Checkbox, Avatar } from "@/components/vb";
import { projectsInstance } from "@/services/ProjectsService";
import { vbClient } from "@/services/vb/vbClient";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import { normalizeUrl, validateUrl } from "@/lib/utils";
import { useWorkspace } from "@/context/WorkspaceContext";
import { createWorkspace } from "@/services/workspaceService";
import CreateWorkspaceDialog from "@/components/sidebar/CreateWorkspaceDialog";
import { toast } from "sonner";
import { useQueryClient } from "@tanstack/react-query";

interface ReportDialogProps {
    open: boolean;
    setOpen: (open: boolean) => void;
    onClick?: (open: boolean, reportId: number) => void;
    initialCompanyName?: string;
    initialCompanyUrl?: string;
}

/** Numbered step heading — the two halves of the form (company details, then
 *  workspace) read as a sequence instead of one undifferentiated stack. */
const StepHeading = ({ step, label }: { step: number; label: string }) => (
    <div className="flex items-center gap-2">
        <span className="grid h-5 w-5 shrink-0 place-items-center rounded-vb-pill bg-vb-brand text-[11px] font-bold text-vb-on-brand">
            {step}
        </span>
        <span className="text-[11px] font-bold uppercase tracking-[0.14em] text-vb-text-3">
            {label}
        </span>
    </div>
);

export const ReportDialog = ({ open, setOpen, onClick, initialCompanyName, initialCompanyUrl }: ReportDialogProps) => {
    const { token, fetchProfile } = useAuth();
    const queryClient = useQueryClient();
    const { activeWorkspace, workspaces, refreshWorkspaces, setActiveWorkspace, billingAccount } = useWorkspace();
    const [companyName, setCompanyName] = useState<string>('');
    const [companyUrl, setCompanyUrl] = useState<string>('');
    const [urlError, setUrlError] = useState<string | null>(null);
    const [recurringMonthly, setRecurringMonthly] = useState<boolean>(false);
    const [selectedWorkspaceId, setSelectedWorkspaceId] = useState<string>('');
    const [submitting, setSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState<string | null>(null);
    const [createWorkspaceOpen, setCreateWorkspaceOpen] = useState(false);
    const [createWorkspaceLoading, setCreateWorkspaceLoading] = useState(false);
    const navigate = useNavigate();
    const CREATE_WORKSPACE_OPTION_VALUE = "__create_workspace__";
    const ONBOARDING_NOTICE_MESSAGE = "You need to onboard yourself first.";

    const selectedWorkspace = workspaces.find(
        (workspace) => String(workspace.id) === selectedWorkspaceId,
    ) ?? null;

    // Credits are held by the BILLING ACCOUNT, not the workspace, and the
    // context only carries the active workspace's account — so the indicator is
    // shown for the workspaces on that same account and omitted elsewhere
    // rather than guessed. It is display only: nothing here gates submission
    // (the zero-credit path is the server's, and it still returns a report).
    const accountCredits = billingAccount
        ? billingAccount.total_credits ?? billingAccount.credits
        : null;
    const creditsFor = (billingAccountId: number): number | null =>
        billingAccount && billingAccount.id === billingAccountId ? accountCredits : null;

    // Reset the form on each open to a deterministic state: the company fields
    // seed from the caller (the sidebar "+" prefills the selected company; the
    // landing flow prefills its query params) and are otherwise blank. Kept off
    // `activeWorkspace` so creating a workspace mid-dialog (below) can't wipe a
    // half-typed company — that lives in its own effect.
    useEffect(() => {
        if (!open) return;
        setCompanyName(initialCompanyName ?? "");
        setCompanyUrl(initialCompanyUrl ?? "");
        setRecurringMonthly(false);
        setUrlError(null);
        setSubmitError(null);
    }, [open, initialCompanyName, initialCompanyUrl]);

    // Default the workspace to the active one — and follow it when a workspace is
    // created from inside the dialog (handleCreateWorkspace flips the active one).
    useEffect(() => {
        if (!open) return;
        setSelectedWorkspaceId(activeWorkspace ? String(activeWorkspace.id) : "");
    }, [open, activeWorkspace]);

    const handleUrlChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setCompanyUrl(e.target.value);
        if (urlError) setUrlError(null);
    };

    const handleUrlBlur = () => {
        if (!companyUrl.trim()) {
            setUrlError(null);
            return;
        }
        const error = validateUrl(companyUrl);
        setUrlError(error);
    };

    // Warm the buyer-side company context as soon as a company is added, so the
    // Virtual Buyer wizard is instant when the user first opens it for this
    // company. Fire-and-forget and silent: it's idempotent, and the wizard
    // re-ensures (behind its own loading state) on entry if this didn't finish
    // or failed — so a failure here is harmless.
    const warmCompanyContext = (companyId?: number) => {
        if (companyId == null) return;
        void vbClient.ensureCompanyContext(companyId, token).catch(() => {});
    };

    const handleCreate = async () => {
        if (!selectedWorkspace) {
            setSubmitError("Please select a workspace.");
            return;
        }

        const sanitizedUrl = normalizeUrl(companyUrl);
        const error = validateUrl(sanitizedUrl);
        if (error) {
            setUrlError(error);
            return;
        }
        setSubmitting(true);
        setSubmitError(null);
        localStorage.removeItem("landingRef");
        try {
            const result = await projectsInstance.createReport(
                companyName,
                normalizeUrl(companyUrl),
                recurringMonthly,
                token,
                selectedWorkspace.id,
            );

            if (result.success && result.reportId) {
                await queryClient.invalidateQueries({ queryKey: ["reports"] });
                await fetchProfile();
                // Pull the just-created company into the workspace/company list so
                // the sidebar selector reflects it (newly added companies aren't in
                // the cached context until we refresh).
                await refreshWorkspaces();
                warmCompanyContext(result.companyId);
                onClick?.(true, result.reportId);
                setSubmitting(false);
                setOpen(false);
                // Open the company that was just added, not `/` — which resolves
                // to whichever company was last opened and would drop the user
                // somewhere they didn't ask for (AITS-540). Matches the branch
                // below. The report is still generating; the dashboard shows a
                // "still building" state under the progress dialog.
                navigate(result.companyId ? `/reports/${result.companyId}` : "/");
            } else if (!result.success && result.reportId && result.companyId) {
                await queryClient.invalidateQueries({ queryKey: ["reports"] });
                // Same as above: make the new company visible to the sidebar
                // before we land on its report view.
                await refreshWorkspaces();
                warmCompanyContext(result.companyId);
                setSubmitting(false);
                setOpen(false);
                navigate(`/reports/${result.companyId}/${result.reportId}`);
            } else if (!result.success) {
                if (result.code === "billing_onboarding_required" || result.onboardingRequired) {
                    setSubmitting(false);
                    setOpen(false);
                    navigate("/onboarding", {
                        replace: true,
                        state: { onboardingNotice: ONBOARDING_NOTICE_MESSAGE },
                    });
                    return;
                }
                setSubmitError(result.message || "Something went wrong. Please try again.");
                setSubmitting(false);
            }
        } catch (e: any) {
            console.error("Failed to create report:", e);
            setSubmitError(e?.message || "Something went wrong. Please try again.");
            setSubmitting(false);
        }
    };

    const handleWorkspaceSelectionChange = (value: string) => {
        if (value === CREATE_WORKSPACE_OPTION_VALUE) {
            setCreateWorkspaceOpen(true);
            return;
        }

        setSelectedWorkspaceId(value);
        setSubmitError(null);
    };

    const handleCreateWorkspace = async (workspaceName: string) => {
        try {
            setCreateWorkspaceLoading(true);
            const createdWorkspace = await createWorkspace(workspaceName);
            await refreshWorkspaces();
            setActiveWorkspace(createdWorkspace);
            setSelectedWorkspaceId(String(createdWorkspace.id));
            setCreateWorkspaceOpen(false);
            setSubmitError(null);
            toast.success("Workspace created");
        } catch (error) {
            const err = error as Error & { code?: string; onboardingRequired?: boolean };
            if (err.code === "billing_onboarding_required" || err.onboardingRequired) {
                setCreateWorkspaceOpen(false);
                setOpen(false);
                navigate("/onboarding", {
                    replace: true,
                    state: { onboardingNotice: ONBOARDING_NOTICE_MESSAGE },
                });
                return;
            }

            const message = err.message || "Failed to create workspace";
            toast.error(message);
        } finally {
            setCreateWorkspaceLoading(false);
        }
    };


    return (
        <>
            <Modal
                open={open}
                onOpenChange={setOpen}
                title="Add a report for a company"
                description={
                    <>
                        Discover how your company is ranked and portrayed by AI models using key{" "}
                        <span className="text-vb-brand">AI Trust Signals</span>.
                    </>
                }
                widthClassName="max-w-[520px]"
                bodyClassName="-mx-1 px-1"
                footer={
                    <VbButton
                        onClick={handleCreate}
                        disabled={
                            submitting ||
                            !companyName ||
                            !companyUrl ||
                            !!urlError ||
                            !selectedWorkspace
                        }
                    >
                        {submitting ? "Checking…" : "Check my AI Trust Signals Score"}
                    </VbButton>
                }
            >
                <div className="flex flex-col gap-5">
                    <div className="flex justify-center">
                        <img
                            src="/createReport.png"
                            alt="create report illustration"
                            className="h-auto w-auto max-h-[104px] max-w-full"
                        />
                    </div>

                    {/* ── Step 1 · company details ─────────────────────────── */}
                    <section className="flex flex-col gap-3">
                        <StepHeading step={1} label="Company details" />

                        <Field label="Company Name" id="companyName">
                            <Input
                                value={companyName}
                                onChange={(e) => setCompanyName(e.target.value)}
                                placeholder="name..."
                            />
                        </Field>

                        <Field
                            label="Company Website"
                            id="companyUrl"
                            hint={
                                <>
                                    To ensure report accuracy, please provide the{" "}
                                    <b>full website address</b>, starting with www - for example:{" "}
                                    <u>www.company.com</u>
                                </>
                            }
                            error={urlError}
                        >
                            <Input
                                value={companyUrl}
                                onChange={handleUrlChange}
                                onBlur={handleUrlBlur}
                                placeholder="www..."
                            />
                        </Field>

                        <div className="rounded-vb-md border border-vb-line bg-vb-bg-subtle px-3 py-2.5">
                            <Checkbox
                                checked={recurringMonthly}
                                onChange={(e) => setRecurringMonthly(e.target.checked)}
                            >
                                I want this report to be generated every month
                            </Checkbox>
                        </div>
                    </section>

                    {/* ── Step 2 · workspace ───────────────────────────────── */}
                    <section className="flex flex-col gap-3">
                        <StepHeading step={2} label="Workspace" />

                        <div
                            role="radiogroup"
                            aria-label="Select workspace for report generation"
                            className="flex flex-col gap-2"
                        >
                            {workspaces.map((workspace) => {
                                const selected = String(workspace.id) === selectedWorkspaceId;
                                const credits = creditsFor(workspace.billing_account_id);
                                return (
                                    <button
                                        key={workspace.id}
                                        type="button"
                                        role="radio"
                                        aria-checked={selected}
                                        onClick={() =>
                                            handleWorkspaceSelectionChange(String(workspace.id))
                                        }
                                        className={[
                                            "flex w-full items-center gap-3 rounded-vb-md border px-3 py-2.5 text-left transition-colors",
                                            selected
                                                ? "border-vb-brand bg-vb-brand-soft"
                                                : "border-vb-line bg-vb-surface hover:bg-vb-bg-subtle",
                                        ].join(" ")}
                                    >
                                        <span
                                            aria-hidden="true"
                                            className={[
                                                "grid h-[18px] w-[18px] shrink-0 place-items-center rounded-vb-pill border-[1.5px]",
                                                selected
                                                    ? "border-vb-brand"
                                                    : "border-vb-line-strong",
                                            ].join(" ")}
                                        >
                                            <span
                                                className={[
                                                    "h-2 w-2 rounded-vb-pill",
                                                    selected ? "bg-vb-brand" : "bg-transparent",
                                                ].join(" ")}
                                            />
                                        </span>
                                        <Avatar
                                            kind="workspace"
                                            name={workspace.name}
                                            size="sm"
                                            decorative
                                        />
                                        <span className="min-w-0 flex-1 truncate text-[13.5px] font-semibold text-vb-text">
                                            {workspace.name}
                                        </span>
                                        {credits != null && (
                                            <span className="shrink-0 text-[12px] font-semibold text-vb-text-3">
                                                {credits} {credits === 1 ? "credit" : "credits"}
                                            </span>
                                        )}
                                    </button>
                                );
                            })}
                        </div>

                        {/* Sentinel row — same option value the Select carried, so it
                            still routes into CreateWorkspaceDialog. Deliberately a
                            SIBLING of the radiogroup, not a child: a radiogroup's
                            owned children must all be radios, and this is an action.
                            Disabled when there is no workspace, exactly as the old
                            `disabled={workspaces.length === 0}` Select was. */}
                        <button
                            type="button"
                            onClick={() =>
                                handleWorkspaceSelectionChange(CREATE_WORKSPACE_OPTION_VALUE)
                            }
                            disabled={workspaces.length === 0}
                            className="flex w-full items-center gap-2 rounded-vb-md border border-dashed border-vb-line-strong px-3 py-2.5 text-left text-[13px] font-semibold text-vb-brand transition-colors hover:bg-vb-brand-soft disabled:cursor-not-allowed disabled:text-vb-text-3 disabled:hover:bg-transparent"
                        >
                            <Plus size={15} aria-hidden="true" />
                            Add New Workspace
                        </button>

                        {workspaces.length === 0 && (
                            <Callout
                                tone="warn"
                                role="status"
                                icon={<AlertTriangle className="h-[15px] w-[15px]" aria-hidden="true" />}
                            >
                                No workspace available
                            </Callout>
                        )}
                    </section>

                    <p className="text-[12px] text-vb-text-3">
                        Proceeding indicates your acceptance of the{" "}
                        <a href="/disclaimer" target="_blank" className="underline hover:text-vb-text-2">
                            disclaimer
                        </a>
                        .
                    </p>

                    {submitError && (
                        <Callout tone="danger" role="alert">
                            {submitError}
                        </Callout>
                    )}
                </div>
            </Modal>
            <CreateWorkspaceDialog
                open={createWorkspaceOpen}
                onOpenChange={setCreateWorkspaceOpen}
                onCreate={handleCreateWorkspace}
                loading={createWorkspaceLoading}
            />
        </>
    );
};

export default ReportDialog;
